import React, { useCallback, useEffect, useMemo, useState } from 'react';
import API_BASE_URL from '../config/api';
import { API_ENDPOINTS } from '../config/api';
import './AdminAcademicYearAdvance.css';

export default function AdminAcademicYearAdvance() {
  const [years, setYears] = useState([]);
  const [targetYear, setTargetYear] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightError, setPreflightError] = useState('');
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [programOutcomes, setProgramOutcomes] = useState([]);
  const [outcomeLoading, setOutcomeLoading] = useState(false);
  const [outcomeMessage, setOutcomeMessage] = useState('');
  const [newOutcome, setNewOutcome] = useState({ number: '', description: '' });

  const loadPreflight = useCallback(async (year) => {
    if (!year) { setPreflight(null); return; }
    setPreflightLoading(true); setPreflightError('');
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_ACADEMIC_YEAR_PREFLIGHT(year), {
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Ön kontrol çalıştırılamadı.');
      setPreflight(data);
    } catch (err) {
      setPreflight(null);
      setPreflightError(err.message || 'Ön kontrol çalıştırılamadı.');
    } finally { setPreflightLoading(false); }
  }, []);

  const loadYears = useCallback(() => {
    fetch(`${API_BASE_URL}/api/academic-years`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => {
        setYears(Array.isArray(data) ? data : []);
        const current = data.find((year) => year.is_current);
        const planningYear = data.find((year) => year.status === 'planning');
        if (current && /^\d{4}$/.test(current.year)) setTargetYear(String(Number(current.year) + 1));
        if (planningYear) loadPreflight(planningYear.year);
        else setPreflight(null);
      })
      .catch(() => setError('Akademik yıl bilgileri yüklenemedi.'));
  }, [loadPreflight]);

  useEffect(() => { loadYears(); }, [loadYears]);

  const source = useMemo(() => years.find((year) => year.is_current), [years]);
  const planning = useMemo(() => years.find((year) => year.status === 'planning'), [years]);

  useEffect(() => {
    if (!planning) {
      setPrograms([]); setSelectedProgramId(''); setProgramOutcomes([]);
      return;
    }
    fetch(`${API_BASE_URL}/api/programs`, {
      headers: { 'X-Academic-Year': planning.year },
    })
      .then((response) => response.ok ? response.json() : Promise.reject())
      .then((data) => setPrograms(Array.isArray(data) ? data : []))
      .catch(() => setOutcomeMessage('Program listesi yüklenemedi.'));
  }, [planning]);

  const loadProgramOutcomes = useCallback(async (programId, year) => {
    if (!programId || !year) { setProgramOutcomes([]); return; }
    setOutcomeLoading(true); setOutcomeMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/program-outcomes/${programId}`, {
        headers: { 'X-Academic-Year': year },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'PÇ listesi yüklenemedi.');
      setProgramOutcomes((data.outcomes || []).map((item) => ({
        ...item,
        number: String(item.code || '').replace(/^PÇ/i, ''),
      })));
    } catch (err) {
      setProgramOutcomes([]);
      setOutcomeMessage(err.message || 'PÇ listesi yüklenemedi.');
    } finally { setOutcomeLoading(false); }
  }, []);

  useEffect(() => {
    loadProgramOutcomes(selectedProgramId, planning?.year);
  }, [selectedProgramId, planning, loadProgramOutcomes]);

  const saveOutcome = async (outcome) => {
    if (!planning || !selectedProgramId) return;
    setOutcomeLoading(true); setOutcomeMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/program-outcomes/item/${outcome.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Academic-Year': planning.year },
        body: JSON.stringify({ outcome: outcome.number, description: outcome.description }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'PÇ kaydedilemedi.');
      setOutcomeMessage(`PÇ${outcome.number} hedef yılda güncellendi.`);
      await loadProgramOutcomes(selectedProgramId, planning.year);
      loadPreflight(planning.year);
    } catch (err) { setOutcomeMessage(err.message || 'PÇ kaydedilemedi.'); }
    finally { setOutcomeLoading(false); }
  };

  const addOutcome = async () => {
    if (!planning || !selectedProgramId || !newOutcome.number.trim() || !newOutcome.description.trim()) return;
    setOutcomeLoading(true); setOutcomeMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/program-outcomes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Academic-Year': planning.year },
        body: JSON.stringify({
          program_id: Number(selectedProgramId),
          outcome: newOutcome.number.trim(),
          description: newOutcome.description.trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'PÇ eklenemedi.');
      setNewOutcome({ number: '', description: '' });
      setOutcomeMessage('Yeni PÇ yalnızca hedef yıla eklendi.');
      await loadProgramOutcomes(selectedProgramId, planning.year);
      loadPreflight(planning.year);
    } catch (err) { setOutcomeMessage(err.message || 'PÇ eklenemedi.'); }
    finally { setOutcomeLoading(false); }
  };

  const deleteOutcome = async (outcome) => {
    if (!planning || !window.confirm(`PÇ${outcome.number} hedef yıl listesinden silinsin mi?`)) return;
    setOutcomeLoading(true); setOutcomeMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/program-outcomes/item/${outcome.id}`, {
        method: 'DELETE', headers: { 'X-Academic-Year': planning.year },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'PÇ silinemedi.');
      setOutcomeMessage('PÇ yalnızca hedef yıl listesinden silindi.');
      await loadProgramOutcomes(selectedProgramId, planning.year);
      loadPreflight(planning.year);
    } catch (err) { setOutcomeMessage(err.message || 'PÇ silinemedi.'); }
    finally { setOutcomeLoading(false); }
  };
  const create = async () => {
    setError(''); setMessage('');
    if (!source) return setError('Aktif akademik yıl bulunamadı.');
    if (!/^\d{4}$/.test(targetYear)) return setError('Hedef yılı dört haneli girin.');
    if (!confirmed) return setError('Devam etmeden önce onay kutusunu işaretleyin.');
    setSaving(true);
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_ADVANCE_ACADEMIC_YEAR, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_year: source.year, target_year: targetYear })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'İşlem tamamlanamadı.');
      setConfirmed(false);
      setMessage(data.message);
      loadYears();
    } catch (err) { setError(err.message || 'Sunucuya bağlanılamadı.'); }
    finally { setSaving(false); }
  };

  const activate = async () => {
    setError(''); setMessage('');
    if (!planning) return setError('Aktifleştirilecek hazırlık yılı bulunamadı.');
    if (!confirmed) return setError('Devam etmeden önce onay kutusunu işaretleyin.');
    setSaving(true);
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_ACTIVATE_ACADEMIC_YEAR, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_year: planning.year })
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.preflight) setPreflight(data.preflight);
        throw new Error(data.error || 'Aktifleştirme tamamlanamadı.');
      }
      localStorage.setItem('academicYear', data.year);
      setMessage(`${data.message} Sayfa yenileniyor…`);
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) { setError(err.message || 'Sunucuya bağlanılamadı.'); }
    finally { setSaving(false); }
  };

  return <section className="year-advance-card">
    <span className="year-advance-eyebrow">Yönetici İşlemi</span>
    <h2>Yeni Akademik Yıl Oluştur</h2>
    <p>Yeni yıl önce hazırlık durumunda açılır. Kaynak PÇ listesi yeni kimliklerle kopyalanır; mevcut yıl tüm kontroller tamamlanana kadar aktif kalır.</p>
    <div className="year-advance-grid">
      <label>Kaynak yıl<input value={source?.label || 'Yükleniyor…'} readOnly /></label>
      <label>Yeni yıl<input value={planning?.year || targetYear} disabled={Boolean(planning)} onChange={(e) => setTargetYear(e.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" placeholder="2027" /></label>
    </div>
    <div className="year-advance-notice"><strong>1. Eski yılı kapat:</strong> Dönem Sonuçları ekranından bütün dersleri kesinleştirin ve hatalı PÇ bağlantılarını düzeltin.<br /><strong>2. Hazırla:</strong> Yeni yıl açılır; kaynak PÇ’ler hedef yıla kopyalanır. Hedef PÇ listesini kontrol edip gerekiyorsa yalnızca hedef yılda değiştirin.<br /><strong>3. Analiz:</strong> Hazırlık yılını seçip akademisyen Excel’ini yükleyin.<br /><strong>4. EBS:</strong> Ders planı Excel’ini önizleyip uygulayın ve ders–akademisyen atamalarını tamamlayın.<br /><strong>5. Aktifleştir:</strong> Ön kontrol geçince eski yıl arşivlenir ve yeni yıl aktif olur. ÖÇ–PÇ eşleştirmelerini bundan sonra akademisyenler yapar.</div>
    {planning && <div className="year-advance-notice"><strong>{planning.label} hazırlanıyor.</strong> Aşağıdaki ön kontroldeki kritik eksikleri tamamlayın. ÖÇ–PÇ eşleştirmeleri aktivasyondan sonra akademisyenler tarafından yapılacaktır.</div>}
    {planning && <div className="planning-outcome-editor">
      <div className="year-preflight-header">
        <div><span className="year-advance-eyebrow">Hedef Yıl PÇ Listesi</span><h3>{planning.label}</h3></div>
        <select value={selectedProgramId} onChange={(event) => setSelectedProgramId(event.target.value)}>
          <option value="">Program seçin</option>
          {programs.map((program) => <option key={program.id} value={program.id}>{program.name}</option>)}
        </select>
      </div>
      {outcomeMessage && <p className="year-outcome-message">{outcomeMessage}</p>}
      {selectedProgramId && <>
        <div className="planning-outcome-list">
          {programOutcomes.map((outcome, index) => <div className="planning-outcome-row" key={outcome.id}>
            <input aria-label="PÇ numarası" value={outcome.number} onChange={(event) => {
              const next = [...programOutcomes]; next[index] = { ...outcome, number: event.target.value }; setProgramOutcomes(next);
            }} />
            <textarea aria-label={`PÇ${outcome.number} açıklaması`} value={outcome.description || ''} onChange={(event) => {
              const next = [...programOutcomes]; next[index] = { ...outcome, description: event.target.value }; setProgramOutcomes(next);
            }} />
            <button type="button" disabled={outcomeLoading} onClick={() => saveOutcome(outcome)}>Kaydet</button>
            <button type="button" className="is-danger" disabled={outcomeLoading} onClick={() => deleteOutcome(outcome)}>Sil</button>
          </div>)}
        </div>
        <div className="planning-outcome-row is-new">
          <input aria-label="Yeni PÇ numarası" placeholder="No" value={newOutcome.number} onChange={(event) => setNewOutcome({ ...newOutcome, number: event.target.value })} />
          <textarea aria-label="Yeni PÇ açıklaması" placeholder="Yeni program çıktısı açıklaması" value={newOutcome.description} onChange={(event) => setNewOutcome({ ...newOutcome, description: event.target.value })} />
          <button type="button" disabled={outcomeLoading || !newOutcome.number.trim() || !newOutcome.description.trim()} onClick={addOutcome}>PÇ Ekle</button>
        </div>
      </>}
    </div>}
    {planning && <div className="year-preflight">
      <div className="year-preflight-header">
        <div><span className="year-advance-eyebrow">Aktivasyon Ön Kontrolü</span><h3>{planning.label}</h3></div>
        <button type="button" onClick={() => loadPreflight(planning.year)} disabled={preflightLoading}>
          {preflightLoading ? 'Kontrol ediliyor…' : 'Kontrolü Yenile'}
        </button>
      </div>
      {preflightError && <p className="year-advance-error">{preflightError}</p>}
      {preflight && <>
        <div className={`year-preflight-summary ${preflight.ready ? (preflight.warning_count ? 'has-warnings' : 'is-ready') : 'has-errors'}`}>
          <strong>{preflight.ready
            ? (preflight.warning_count ? `Aktivasyona hazır · ${preflight.warning_count} takip uyarısı` : 'Aktivasyona hazır')
            : `${preflight.blocking_count} kritik eksik var`}</strong>
          <span>{preflight.ready
            ? (preflight.warning_count
              ? 'Zorunlu kontroller geçti; eksik PÇ listeleri, akademisyen ve ders atamaları aktivasyondan sonra tamamlanabilir.'
              : 'Tüm zorunlu kontroller geçti.')
            : 'Kırmızı kontroller düzeltilmeden yıl aktifleştirilemez.'}</span>
        </div>
        <div className="year-preflight-list">
          {preflight.checks.map((check) => <div key={check.id} className={`year-preflight-item status-${check.status}`}>
            <span className="year-preflight-icon" aria-hidden="true">{check.status === 'pass' ? '✓' : check.status === 'error' ? '!' : 'i'}</span>
            <div><strong>{check.label}</strong><p>{check.message}</p>
              {check.details?.length > 0 && <ul>{check.details.map((detail, index) => (
                <li key={`${check.id}-${detail.program_id || detail.id || detail.course_id || index}`}>
                  {[detail.program_name, detail.course_code, detail.course_name].filter(Boolean).join(' — ') || JSON.stringify(detail)}
                </li>
              ))}</ul>}
            </div>
          </div>)}
        </div>
      </>}
    </div>}
    <label className="year-advance-confirm"><input type="checkbox" checked={confirmed} disabled={Boolean(planning && !preflight?.ready)} onChange={(e) => setConfirmed(e.target.checked)} /> {planning ? `${planning.label} ön kontrolünün tamamlandığını gördüm ve yılı aktifleştirmek istiyorum.` : 'Yeni hazırlık yılını oluşturmak istiyorum.'}</label>
    {error && <p className="year-advance-error">{error}</p>}
    {message && <p className="year-advance-success">{message}</p>}
    {planning ?
      <button className="year-advance-button" onClick={activate} disabled={saving || preflightLoading || !preflight?.ready}>{saving ? 'Aktifleştiriliyor…' : `${planning.label} Yılını Aktifleştir`}</button> :
      <button className="year-advance-button" onClick={create} disabled={saving || !source}>{saving ? 'Oluşturuluyor…' : 'Hazırlık Yılını Oluştur'}</button>}
  </section>;
}
